/**
 * SVG filter for liquid glass backdrop refraction + dynamic chromatic aberration.
 *
 * Filter chain:
 *   feTurbulence → feGaussianBlur → feDisplacementMap (organic refraction)
 *   → feColorMatrix×3 (RGB split) → feOffset×2 (R/B shift) → feComposite×2 (recompose)
 *
 * The chromatic aberration simulates wavelength-dependent refraction:
 *   - Red (long λ) shifts one direction
 *   - Green (reference λ) stays in place
 *   - Blue (short λ) shifts the opposite direction
 *
 * When `hoverState` is provided, a rAF loop drives the offsets dynamically
 * based on mouse position, creating interactive chromatic dispersion.
 */

import { useRef, useEffect } from 'react';
import type { GlassHoverState } from './useGlassHover';
import { svgFilterContainer } from '../../styles/layout.css';
import { useMediaQuery } from '../../../lib/useMediaQuery';
import { useReducedMotion } from '../../../lib';
import { mobile as mobileQuery } from '../../theme/media';

interface LiquidGlassFilterProps {
  /** SVG filter id. Defaults to 'liquid-glass-refract'. */
  filterId?: string;
  /** Enable animation. Disable for prefers-reduced-motion. */
  animate?: boolean;
  /** Glass hover state ref for mouse-driven chromatic aberration. */
  hoverState?: React.RefObject<GlassHoverState>;
}

export function LiquidGlassFilter(props: LiquidGlassFilterProps) {
  const isMobile = useMediaQuery(mobileQuery);

  // Mobile: skip SVG filter entirely — no rAF loop, no DOM overhead
  if (isMobile) return null;
  return <LiquidGlassFilterInner {...props} />;
}

function LiquidGlassFilterInner({
  filterId = 'liquid-glass-refract',
  animate = true,
  hoverState,
}: LiquidGlassFilterProps) {
  const redOffsetRef = useRef<SVGFEOffsetElement>(null);
  const blueOffsetRef = useRef<SVGFEOffsetElement>(null);
  const lerpRef = useRef({ rdx: 0, rdy: 0, bdx: 0, bdy: 0 });
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!animate || reducedMotion) return;

    let rafId: number;
    const LERP = 0.06;
    // 마지막으로 DOM 에 쓴 문자열. filter 하위 속성이 하나라도 바뀌면 위 체인 전체가 다시
    // 구워지므로, 같은 값을 다시 쓰는 것만으로도 매 프레임 풀스크린 재래스터가 발생한다.
    const last = { rdx: '', rdy: '', bdx: '', bdy: '' };

    const tick = () => {
      const cur = lerpRef.current;
      const t = performance.now() * 0.001;

      let hoverMul = 0;
      let mx = 0;
      let my = 0;

      if (hoverState?.current) {
        const hs = hoverState.current;
        hoverMul = hs.hover ? 1.0 : 0.0;
        mx = (hs.mouseX - 0.5) * 2.0; // -1 … +1
        my = (hs.mouseY - 0.5) * 2.0;
      }

      // Breathing — very subtle drift so edges shimmer. hover 중에만 적용한다.
      // idle 진폭은 intensity(0.15) × 0.06 = 0.009px 로 눈에 보이지 않는 반면, 타깃이 매
      // 프레임 흔들려 아래 lerp 가 영원히 수렴하지 못하고 DOM 쓰기가 멈추지 않았다.
      const breatheX = hoverMul * Math.sin(t * 0.2) * 0.06;
      const breatheY = hoverMul * Math.cos(t * 0.15) * 0.04;

      // idle 0.15px, hover ramps to 0.5px — just enough for edge fringe
      const intensity = 0.15 + hoverMul * 0.35;

      // Red: slight shift with mouse direction
      const trdx = (1.0 + mx * 0.3 + breatheX) * intensity;
      const trdy = (0.3 + my * 0.15 + breatheY) * intensity;

      // Blue: opposite direction
      const tbdx = (-1.0 - mx * 0.15 + breatheX) * intensity;
      const tbdy = (-0.3 - my * 0.08 + breatheY) * intensity;

      // Smooth lerp
      cur.rdx += (trdx - cur.rdx) * LERP;
      cur.rdy += (trdy - cur.rdy) * LERP;
      cur.bdx += (tbdx - cur.bdx) * LERP;
      cur.bdy += (tbdy - cur.bdy) * LERP;

      // DOM 이 보는 것은 포맷된 문자열뿐이니 같으면 쓰지 않는다. idle 에서 lerp 가 수렴하면
      // 쓰기가 완전히 멈춰 필터 재래스터 비용이 0 이 되고, hover 로 값이 다시 움직이면
      // 저절로 재개되므로 루프를 세웠다 깨우는 배선이 필요 없다.
      const red = redOffsetRef.current;
      if (red) {
        const rdx = cur.rdx.toFixed(3);
        const rdy = cur.rdy.toFixed(3);
        if (rdx !== last.rdx) {
          last.rdx = rdx;
          red.setAttribute('dx', rdx);
        }
        if (rdy !== last.rdy) {
          last.rdy = rdy;
          red.setAttribute('dy', rdy);
        }
      }
      const blue = blueOffsetRef.current;
      if (blue) {
        const bdx = cur.bdx.toFixed(3);
        const bdy = cur.bdy.toFixed(3);
        if (bdx !== last.bdx) {
          last.bdx = bdx;
          blue.setAttribute('dx', bdx);
        }
        if (bdy !== last.bdy) {
          last.bdy = bdy;
          blue.setAttribute('dy', bdy);
        }
      }

      // ponytail: 수렴 후에도 rAF 루프 자체는 계속 돈다 (부동소수 연산 몇 개 = 사실상 무료).
      // hoverState 가 ref 라 hover 진입 시 루프를 깨울 이벤트가 없어서 — 필요해지면
      // useGlassHover 에 구독을 붙이고 여기서 루프를 정지시키면 된다.
      rafId = requestAnimationFrame(tick);
    };

    const idleId = requestIdleCallback(() => {
      rafId = requestAnimationFrame(tick);
    });
    return () => {
      cancelIdleCallback(idleId);
      cancelAnimationFrame(rafId);
    };
  }, [animate, hoverState, reducedMotion]);

  return (
    <svg focusable="false" aria-hidden="true" className={svgFilterContainer}>
      <defs>
        <filter
          id={filterId}
          x="-5%"
          y="-5%"
          width="110%"
          height="110%"
          colorInterpolationFilters="sRGB"
        >
          {/* ── 1. Organic noise generation ── */}
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.012 0.015"
            numOctaves={2}
            seed={3}
            result="noise"
          >
            {animate && (
              <animate
                attributeName="baseFrequency"
                values="0.012 0.015;0.014 0.013;0.011 0.016;0.013 0.014;0.012 0.015"
                dur="12s"
                repeatCount="indefinite"
              />
            )}
          </feTurbulence>

          {/* ── 2. Soften noise ── */}
          <feGaussianBlur in="noise" stdDeviation="1" result="smoothNoise" />

          {/* ── 3. Displace backdrop — uniform refraction ── */}
          <feDisplacementMap
            in="SourceGraphic"
            in2="smoothNoise"
            scale={3}
            xChannelSelector="R"
            yChannelSelector="G"
            result="displaced"
          >
            {animate && (
              <animate
                attributeName="scale"
                values="3;4;2;3.5;3"
                dur="8s"
                repeatCount="indefinite"
              />
            )}
          </feDisplacementMap>

          {/* ── 4. Chromatic Aberration — wavelength-dependent refraction ── */}
          {/* Split displaced result into isolated R, G, B channels */}
          <feColorMatrix
            in="displaced"
            type="matrix"
            values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
            result="convergR"
          />
          <feColorMatrix
            in="displaced"
            type="matrix"
            values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0"
            result="convergG"
          />
          <feColorMatrix
            in="displaced"
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0"
            result="convergB"
          />

          {/* Shift R and B in opposite directions — JS rAF loop drives dx/dy */}
          <feOffset ref={redOffsetRef} in="convergR" dx="0.15" dy="0.05" result="convergRShift" />
          {/* G stays unshifted (reference wavelength) */}
          <feOffset
            ref={blueOffsetRef}
            in="convergB"
            dx="-0.15"
            dy="-0.05"
            result="convergBShift"
          />

          {/* Recompose: additive merge of separated channels */}
          <feComposite
            operator="arithmetic"
            k1="0"
            k2="1"
            k3="1"
            k4="0"
            in="convergRShift"
            in2="convergG"
            result="convergRG"
          />
          <feComposite
            operator="arithmetic"
            k1="0"
            k2="1"
            k3="1"
            k4="0"
            in="convergRG"
            in2="convergBShift"
          />
        </filter>
      </defs>
    </svg>
  );
}
